import { tagAbbreviations } from "@aura-frontend/shared";
import clsx from "clsx";
import { XCircle } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import { setTaggingFlowStep } from "../../../../store/slices/hotkeys";
import { setTags } from "../../../../store/slices/session";
import {
  deleteEventFromQueue,
  setFocusedEventPayload,
  setIsViewingPreviousEvent,
} from "../../../../store/slices/socket";
import {
  selectFocusedEvent,
  selectRecentEvent,
} from "../../../../store/slices/socket/selectors";
import type { RootState } from "../../../../store/store";
import type { IQueueEvent } from "../../../../store/types";
import { eventTime } from "../../../../store/utils/eventTime";
import { secondsSinceEvent } from "../../../../store/utils/secondsSinceEvent";

interface EventCardProps {
  event?: IQueueEvent;
}

export const EventCard = ({ event }: EventCardProps) => {
  const dispatch = useDispatch();
  const gameState = useSelector((state: RootState) => state.game.gameState);
  const recentEvent = useSelector(selectRecentEvent);
  const focusedEvent = useSelector(selectFocusedEvent);
  const { isViewingPreviousEvent } = useSelector(
    (state: RootState) => state.socket,
  );
  const away = useSelector((state: RootState) => state.game.rosters.away);
  const home = useSelector((state: RootState) => state.game.rosters.home);
  const { inning } = gameState;

  const activeEvent = isViewingPreviousEvent
    ? focusedEvent.event_uuid
    : recentEvent.event_uuid;

  const active = event
    ? event.contributing_events?.includes(activeEvent)
    : false;

  const { pitchResult, playResult, pitchType, contactType } =
    event?.tagData ?? {};

  const currentPitcher =
    inning.half === "top" ? home.currentPitcher : away.currentPitcher;
  const currentHitter =
    inning.half === "top" ? away.currentHitter : home.currentHitter;
  const currentCatcher =
    inning.half === "top" ? home.currentCatcher : away.currentCatcher;

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (event?.event_uuid) {
      dispatch(deleteEventFromQueue(event.event_uuid));
      dispatch(setIsViewingPreviousEvent(false));
      dispatch(setFocusedEventPayload({}));
    }
  };

  const handleUntaggedClick = () => {
    if (!event) return;
    dispatch(setIsViewingPreviousEvent(true));
    dispatch(
      setFocusedEventPayload({
        event_uuid: event.event_uuid,
        gameState: gameState,
        focusedPlayers: {
          focusedPitcher: currentPitcher,
          focusedHitter: currentHitter,
          focusedCatcher: currentCatcher,
        },
      }),
    );
    dispatch(setTaggingFlowStep(0));
    dispatch(setTags({}));
  };

  const handleTaggedClick = () => {
    if (!event || !event.event_uuid) return;
    dispatch(setIsViewingPreviousEvent(true));
    dispatch(
      setFocusedEventPayload({
        event_uuid: event.event_uuid,
        focusedPlayers: event.focusedPlayers,
      }),
    );
    dispatch(setTags(event.tagData));
    dispatch(setTaggingFlowStep(6));
  };

  // Inning indicator + count/outs (right side of tagged/noMetric cards)
  const InningCountSection = () => {
    if (!event?.gameState) return null;

    return (
      <div className="flex items-center gap-1.5">
        {/* Inning indicator column */}
        <div className="flex flex-col items-center">
          <div className="flex h-[6px] items-end">
            {event.gameState.inning?.half === "top" && (
              <div
                className={clsx(
                  // Border
                  "border-b-[6px] border-l-[4px] border-r-[4px]",
                  "border-b-gray-900 border-l-transparent border-r-transparent",
                )}
              />
            )}
          </div>
          <span className="text-sm font-semibold leading-tight text-gray-900">
            {event.gameState.inning.integer}
          </span>
          <div className="flex h-[6px] items-start">
            {event.gameState.inning?.half === "bottom" && (
              <div
                className={clsx(
                  // Border
                  "border-l-[4px] border-r-[4px] border-t-[6px]",
                  "border-l-transparent border-r-transparent border-t-gray-900",
                )}
              />
            )}
          </div>
        </div>

        {/* Count/result + outs */}
        <div className="flex flex-col items-center">
          <span className="text-sm font-semibold text-gray-900">
            {playResult
              ? tagAbbreviations[playResult]
              : `${event.gameState.count.balls}-${event.gameState.count.strikes}`}
          </span>
          <div className="flex gap-0.5">
            {[...Array(event.gameState.outs)].map((_, i) => (
              <div key={i} className="h-2.5 w-2.5 rounded-full bg-black" />
            ))}
            {[...Array(Math.max(0, 2 - event.gameState.outs))].map((_, i) => (
              <div key={i} className="h-2.5 w-2.5 rounded-full bg-gray-300" />
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Untagged events (tagData === null)
  if (event?.tagData === null) {
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div
        onClick={handleUntaggedClick}
        className={clsx(
          // Size
          "h-[60px]",
          // Layout
          "relative flex items-center justify-between",
          // Spacing
          "mx-2 mb-2 p-3",
          // Border
          "rounded border",
          // States
          "cursor-pointer",
          active
            ? "border-blue shadow-[0_0_8px_#0066ff66]"
            : "border-gray-200 bg-white shadow-sm hover:bg-[hsla(218,100%,52%,0.05)]",
        )}
      >
        {active && (
          <button
            onClick={handleDelete}
            className="absolute -left-2 -top-2 z-10 text-blue"
          >
            <XCircle size={16} fill="currentColor" stroke="white" />
          </button>
        )}
        <div className="flex flex-col">
          <span className="text-sm font-semibold">
            {event?.pitch_data?.RelSpeedMPH?.toFixed(1) ?? "---"}
          </span>
          <span className="text-xs text-gray-500">Pitch</span>
        </div>
        <div className="flex flex-col items-center">
          <span className="text-sm font-semibold">Untagged</span>
          <span className="text-xs text-gray-500">
            {secondsSinceEvent(eventTime(event))}
          </span>
        </div>
        <div className="flex flex-col items-end">
          <span className="text-sm font-semibold">
            {event?.hit_data?.ExitSpeedMPH?.toFixed(1) ?? "---"}
          </span>
          <span className="text-xs text-gray-500">EV</span>
        </div>
      </div>
    );
  }

  // No metric events (noMetricEvent === true)
  if (event?.noMetricEvent === true) {
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div
        onClick={handleTaggedClick}
        className={clsx(
          // Size
          "h-[60px]",
          // Layout
          "relative flex items-center justify-between",
          // Spacing
          "mx-2 mb-2 p-3",
          // Border
          "rounded border",
          // States
          "cursor-pointer",
          active
            ? "border-blue shadow-[0_0_8px_#0066ff66]"
            : "border-gray-200 bg-white shadow-sm hover:bg-[hsla(218,100%,52%,0.05)]",
        )}
      >
        {active && (
          <button
            onClick={handleDelete}
            className="absolute -left-2 -top-2 z-10 text-blue"
          >
            <XCircle size={16} fill="currentColor" stroke="white" />
          </button>
        )}
        <div className="flex flex-col">
          <span className="text-sm font-semibold">
            {event?.focusedPlayers?.focusedHitter
              ? `${event.focusedPlayers.focusedHitter.lastName}, ${event.focusedPlayers.focusedHitter.firstName?.charAt(0)}`
              : ""}
          </span>
          <span className="text-xs text-gray-500">No BallFlight Data</span>
        </div>
        <InningCountSection />
      </div>
    );
  }

  // Tagged events (default)
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={handleTaggedClick}
      className={clsx(
        // Size
        "h-[60px]",
        // Layout
        "flex items-center justify-between",
        // Spacing
        "mx-2 mb-2 p-3",
        // Border
        "rounded border",
        // States
        "cursor-pointer",
        active
          ? "border-blue shadow-[0_0_8px_#0066ff66]"
          : "border-gray-200 bg-white shadow-sm hover:bg-[hsla(218,100%,52%,0.05)]",
      )}
    >
      <div className="flex flex-col">
        <span className="text-sm font-semibold">
          {event?.focusedPlayers?.focusedHitter
            ? `${event.focusedPlayers.focusedHitter.lastName}, ${event.focusedPlayers.focusedHitter.firstName?.charAt(0)}`
            : ""}
        </span>
        <span className="text-xs">
          <span className="font-semibold text-blue">
            {`${event?.pitch_data?.RelSpeedMPH?.toFixed(1) ?? "---"} `}
          </span>
          {pitchType ? tagAbbreviations[pitchType] + " " : " "}
          <span className="font-semibold text-blue">
            {contactType
              ? tagAbbreviations[contactType]
              : pitchResult
                ? tagAbbreviations[pitchResult]
                : ""}{" "}
          </span>
        </span>
      </div>
      <InningCountSection />
    </div>
  );
};
